import React from "react";
import { PageHero, Section } from "../../components/Page";
import { SubNav, ABOUT_NAV } from "../../components/SubNav";
import { useLang } from "../../i18n/LanguageContext";
import { useApi } from "../../hooks/useApi";

export function About() {
  const { t } = useLang();
  return (
    <div data-testid="about-page">
      <SubNav items={ABOUT_NAV} testId="about-subnav" />
      <PageHero eyebrow="Our Story · Since 2010" title="A heritage of unity"
        subtitle="From humble beginnings to a thriving association of Okuapem sons and daughters across Massachusetts."
        image="https://images.unsplash.com/photo-1622487560941-7f6bd5ed24b6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" />
      <Section>
        <div className="grid gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6 text-foreground/80">
            <div>
              <h2 className="font-serif-display text-3xl font-bold text-crimson">{t("How we began")}</h2>
              <p className="mt-4 leading-relaxed">{t("Founded in 2010, the Okuapemman Association of Massachusetts was born from the desire of Okuapem migrants to keep their culture alive far from home. A few families gathered to celebrate festivals, support one another through hardship, and give their children a connection to their roots in the Eastern Region of Ghana.")}</p>
            </div>
            <div>
              <h2 className="font-serif-display text-3xl font-bold text-crimson">{t("The unification story")}</h2>
              <p className="mt-4 leading-relaxed">{t("Over the years, smaller gatherings across the Commonwealth came together under one umbrella. This unification strengthened our voice, pooled our welfare resources, and allowed us to speak as one people — \"Biakoye\", unity. Today we stand as one association serving members from Boston to Worcester and beyond.")}</p>
            </div>
            <div>
              <h2 className="font-serif-display text-3xl font-bold text-crimson">{t("Our mission")}</h2>
              <p className="mt-4 leading-relaxed">{t("To preserve and promote Okuapem culture, to provide welfare and mutual support to our members, to nurture the next generation, and to build bridges between our community in Massachusetts and our homeland.")}</p>
            </div>
          </div>
          <aside className="space-y-4">
            <div className="glass rounded-2xl p-6">
              <h3 className="eyebrow text-gold">{t("OKUSACA Affiliation")}</h3>
              <p className="mt-3 text-sm text-foreground/75">{t("We are a proud chapter of the Okuapemman Association of the USA & Canada (OKUSACA), connecting us to a continental family of chapters.")}</p>
            </div>
            <div className="rounded-2xl bg-palm p-6 text-parchment">
              <p className="font-serif-display text-2xl italic text-gold">"Ofie! Biakoye!"</p>
              <p className="mt-2 text-sm text-parchment/80">{t("Home! Unity! — the rallying cry that binds every member of our association.")}</p>
            </div>
          </aside>
        </div>
      </Section>
    </div>
  );
}

export function Membership() {
  const { t } = useLang();
  const { data: sched } = useApi("/dues/schedule");
  const money = (v) => `$${Number(v ?? 0).toFixed(2)}`;
  const benefits = [
    "Welfare support in times of bereavement, sickness, and celebration",
    "Access to our member directory and networking",
    "Participation in cultural festivals (Odwira, Ohum)",
    "Voice and vote in association governance and elections",
    "Scholarship opportunities for members' children",
    "A digital good-standing membership card",
  ];
  return (
    <div data-testid="membership-page">
      <PageHero eyebrow="Join Our Family" title="Membership"
        subtitle="Eligibility, benefits, and how to apply to become part of the Okuapemman family."
        image="https://images.unsplash.com/photo-1737649507334-92c9fa4beb7c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" />
      <Section>
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-serif-display text-3xl font-bold text-crimson">{t("Who can join?")}</h2>
            <p className="mt-4 text-foreground/80">{t("Any person of Okuapem descent (lineage member) or a spouse/associate who shares our values is welcome to apply. We welcome both lineage and associate members.")}</p>
            <h2 className="mt-8 font-serif-display text-3xl font-bold text-crimson">{t("Dues schedule")}</h2>
            <div className="mt-4 space-y-2">
              <div className="flex justify-between rounded-lg bg-parchment-card p-4 border border-gold/30"><span>{t("One-time registration fee")}</span><span className="font-mono-accent font-semibold" data-testid="public-reg-fee">{money(sched?.registration_fee ?? 50)}</span></div>
              <div className="flex justify-between rounded-lg bg-parchment-card p-4 border border-gold/30"><span>{t("Monthly membership dues")}</span><span className="font-mono-accent font-semibold" data-testid="public-monthly-dues">{money(sched?.monthly_dues ?? 20)} / {t("month")} <span className="text-xs text-muted-foreground">({money(sched?.annual_dues ?? 240)} / {t("year")})</span></span></div>
              <p className="text-xs text-muted-foreground">{t("Dues fund welfare, cultural events, and operations. Amounts are approved at the Annual General Meeting. Members 3 months in arrears are placed out of good standing.")}</p>
            </div>
          </div>
          <div className="glass rounded-2xl p-8">
            <h3 className="font-serif-display text-2xl font-bold text-palm">{t("Member benefits")}</h3>
            <ul className="mt-5 space-y-3">
              {benefits.map((b, i) => (
                <li key={i} className="flex gap-3 text-sm text-foreground/80">
                  <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-gold" />{t(b)}
                </li>
              ))}
            </ul>
            <a href="/registration" data-testid="membership-apply-link"
              className="mt-8 block rounded-xl bg-crimson px-6 py-3 text-center font-semibold text-parchment hover:bg-crimson-hover">
              {t("Start your application")}
            </a>
          </div>
        </div>
      </Section>
    </div>
  );
}

export function SimplePage({ testid, eyebrow, title, subtitle, body }) {
  return (
    <div data-testid={testid}>
      <PageHero eyebrow={eyebrow} title={title} subtitle={subtitle} />
      <Section>
        <div className="prose max-w-3xl text-foreground/80">{body}</div>
      </Section>
    </div>
  );
}
